import { FILTERS_COMPONENT_VARIETY } from '@features/regional-overview/domain/tests-id.constants';
import type { Filter } from '@features/regional-overview/regional-overview.models';
import { ALL_FILTER_VALUE, RegionalOverviewFilterType } from '@features/regional-overview/regional-overview.models';
import type { Feature, Geometry } from 'geojson';
import type { RegionalOverviewV2Properties } from 'querys/regional-overview/regional-overview.query.models';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import MultiSelectFilter from '../../components/multi-select-filter/multi-select-filter.component';
import { FILTER_ALL_OPTION_KEY, FILTER_SECTION_TITLE_KEY } from '../../filter-labels.utils';
import { useFiltersStore } from '../../filters.store';

interface VarietySectionProps {
  filter: Filter;
  defaultExpanded?: boolean;
}

const VarietySection = ({ filter, defaultExpanded }: VarietySectionProps) => {
  const [t] = useTranslation();
  const options = useFiltersStore(state => state.options[RegionalOverviewFilterType.VARIETY] ?? []);
  const features = useFiltersStore(state => state.features);
  const raw = useFiltersStore(state => state.values[RegionalOverviewFilterType.VARIETY]);
  const cropsValue = useFiltersStore(state => state.values[RegionalOverviewFilterType.CROPS]);
  const setValue = useFiltersStore(state => state.setValue);

  const narrowedOptions = useMemo(() => {
    if (typeof cropsValue !== 'string' || cropsValue === ALL_FILTER_VALUE) return options;
    const allowed = new Set<string>();
    (features as Feature<Geometry, RegionalOverviewV2Properties>[]).forEach(feature => {
      const values = feature.properties?.values;
      if (values?.crop_name === cropsValue && values.variety_name) {
        allowed.add(values.variety_name);
      }
    });
    return options.filter(option => allowed.has(option.value));
  }, [options, features, cropsValue]);

  const allLabelKey = filter.includeAllOption ? FILTER_ALL_OPTION_KEY[RegionalOverviewFilterType.VARIETY] : undefined;
  const titleKey = FILTER_SECTION_TITLE_KEY[RegionalOverviewFilterType.VARIETY] ?? '';

  const hasNoCropSelected = cropsValue === ALL_FILTER_VALUE;
  const hasNoVarietiesForCrop = !hasNoCropSelected && !narrowedOptions.length;
  const disabledTooltipKey = hasNoCropSelected
    ? 'pages.regional_overview.filters.variety.disabled_tooltip'
    : 'pages.regional_overview.filters.variety.no_varieties_for_crop';

  return (
    <MultiSelectFilter
      filter={RegionalOverviewFilterType.VARIETY}
      title={t(titleKey)}
      options={narrowedOptions}
      allLabelKey={allLabelKey}
      translatable={filter.translatable}
      value={Array.isArray(raw) ? (raw as string[]) : undefined}
      disabled={hasNoCropSelected || hasNoVarietiesForCrop}
      disabledTooltip={t(disabledTooltipKey)}
      onFilterChanged={(_filterType, nextValue) => setValue(RegionalOverviewFilterType.VARIETY, nextValue)}
      defaultExpanded={defaultExpanded}
      dataTestID={FILTERS_COMPONENT_VARIETY}
    />
  );
};

export default VarietySection;
